import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Grid,
  Skeleton,
  IconButton,
} from '@mui/material';
import { COLOR_PRIMARY_2, COLOR_TEXT_1, COLOR_TEXT_2 } from 'src/utils/colors';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import { InfoOutlined, LockRounded } from '@mui/icons-material';

import { useSelector } from 'react-redux';
import { getGradeColor, getGradeAGradient } from 'src/utils/functions';
import InfoButton from 'src/App/Dashboards/Market/Components/Buttons/InfoButton';
import ConfigurableTooltip from 'src/App/Dashboards/Market/Components/Tooltips/ConfigurableTooltip';
import { MARKET_FINDER_TOOLTIP_CONTENT } from 'src/utils/const';

interface MarketGradeAPIResponse {
  market_grade: string;
  market_summary: string[];
  metadata: {
    license_percentage: number;
    region: string;
    trend_value: number;
  };
}

const parseLockTextIntoIcon = (text: string, handleOnSampleClick: (plan: string) => void) => {
  const LockIconComponent = (
    <IconButton size="small" sx={{ p: 0 }} onClick={() => handleOnSampleClick('expert')}>
      <LockRounded color="primary" sx={{ fontSize: 'inherit' }} />
    </IconButton>
  );
  if (text.includes('locked')) {
    const boldSplit = text.split('locked');
    const boldTextList = boldSplit.map((text, idx) => (
      <>
        {text} {idx === boldSplit.length - 1 ? <></> : LockIconComponent}
      </>
    ));
    return boldTextList;
  }
  return text;
};

const renderWithBold = (
  text: string,
  metadata: MarketGradeAPIResponse['metadata'],
  handleOnSampleClick: (plan: string) => void,
  renderAs: 'text' | 'html' = 'html'
) => {
  const regex = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const plain = text.slice(lastIndex, match.index);
      parts.push(renderAs === 'text' ? plain : <span key={key++}>{plain}</span>);
    }

    const boldText = match[1] || match[2];

    if (renderAs === 'text') {
      parts.push(`<b>${boldText}</b>`);
    } else {
      let formattedBoldText: any = boldText;

      if (boldText.includes('regulations')) {
        formattedBoldText = (
          <TooltipNew
            title=""
            content={
              <>
                Regulation status is based on the % of listings with license info. <br /> <br />
                <strong>
                  {metadata.license_percentage >= 1
                    ? `${metadata.license_percentage}`
                    : 'less than 1'}
                  %
                </strong>{' '}
                of listings have a short-term rental license.
              </>
            }
          >
            <span style={{ cursor: 'pointer' }}>
              {parseLockTextIntoIcon(boldText, handleOnSampleClick)}
            </span>
          </TooltipNew>
        );
      } else if (boldText.includes('revenue trend')) {
        formattedBoldText = (
          <TooltipNew
            title=""
            content={
              <>
                {renderWithBold(
                  `Revenue for a typical listing grew **${metadata.trend_value}%** year-over-year.`,
                  metadata,
                  handleOnSampleClick,
                  'html'
                )}
              </>
            }
          >
            <span style={{ cursor: 'pointer' }}>
              {parseLockTextIntoIcon(boldText, handleOnSampleClick)}
            </span>
          </TooltipNew>
        );
      } else {
        formattedBoldText = parseLockTextIntoIcon(boldText, handleOnSampleClick);
      }

      parts.push(<b key={key++}>{formattedBoldText}</b>);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    const rest = text.slice(lastIndex);
    parts.push(renderAs === 'text' ? rest : <span key={key++}>{rest}</span>);
  }

  return renderAs === 'text' ? parts.join('') : parts;
};

const LoadingCard = ({ matches }: { matches: boolean }) => {
  return (
    <Card
      sx={{
        width: '100%',
        background: '#fff',
        borderRadius: 3,
        boxShadow: '0 2px 12px 0 rgba(16,30,54,.06)',
        border: '1px solid #E0E0E0',
        overflow: 'hidden',
        position: 'relative',
        minHeight: matches ? 120 : 140,
      }}
    >
      <CardContent sx={{ p: matches ? 2 : 4, pr: matches ? 2 : 6 }}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: matches ? 80 : 100 }}
        >
          <Grid item xs={3}>
            <Skeleton variant="circular" width={matches ? 70 : 90} height={matches ? 70 : 90} />
          </Grid>
          <Grid item xs={9} container direction="column" gap={matches ? 2 : 3}>
            {[...Array(3)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width="100%"
                height={matches ? 16 : 20}
                sx={{ borderRadius: 4 }}
              />
            ))}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

interface MarketGradeAIOverviewProps {
  handleOnSampleClick: (plan: string) => void;
  setMarketGradeInfo: React.Dispatch<
    React.SetStateAction<{ grade: string; summary: string[] } | null>
  >;
}

const MarketGradeAIOverview: React.FC<MarketGradeAIOverviewProps> = ({
  handleOnSampleClick,
  setMarketGradeInfo,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const marketGrade = useSelector((state: any) => state.dashboard.marketGrade);

  const getGradeScore = (grade: string) => {
    const gradeUpper = grade.toUpperCase();
    if (gradeUpper === 'A') return 100;
    if (gradeUpper === 'B') return 90;
    if (gradeUpper === 'B+') return 80;
    if (gradeUpper === 'C') return 70;
    if (gradeUpper === 'C+') return 60;
    if (gradeUpper === 'D') return 55;
    if (gradeUpper === 'D+') return 50;
    if (gradeUpper === 'E') return 40;
    if (gradeUpper === 'E+') return 30;
    if (gradeUpper === 'F') return 20;
    return 10;
  };

  const grade = marketGrade?.data?.market_grade || '';
  const gradeColor = getGradeColor(grade);
  const gradeScore = getGradeScore(grade);
  const summaryLines = marketGrade?.data?.market_summary || [];
  const metadata = marketGrade?.data?.metadata || {};
  const isGradeA = grade.toUpperCase().startsWith('A');

  const getMetadataTextToAppend = (index: number, prefixText: string) => {
    if (index === 0) {
      // first remove the '.' and then add the region
      return prefixText.slice(0, prefixText.length - 1) + ' in *' + metadata.region + '*.';
    }

    return prefixText;
  };

  const updateMarketGradeInfo = (grade: string, summary: string[]) => {
    if (!summary) {
      return;
    }

    const formattedSummary: any = summary.map((line, index) =>
      renderWithBold(getMetadataTextToAppend(index, line), metadata, handleOnSampleClick, 'text')
    );

    setMarketGradeInfo({ grade, summary: formattedSummary });
  };

  useEffect(() => {
    if (marketGrade.loading || marketGrade.is404Error || marketGrade.isError) {
      setMarketGradeInfo(null);
      return;
    }

    if (marketGrade.data && marketGrade.data.market_grade && marketGrade.data.market_summary) {
      updateMarketGradeInfo(
        marketGrade.data.market_grade.toUpperCase(),
        marketGrade.data.market_summary
      );
      return;
    }

    setMarketGradeInfo(null);
  }, [marketGrade.data, marketGrade.loading, marketGrade.is404Error, marketGrade.isError]);

  // Don't render anything if it's a 404 error
  if (marketGrade.is404Error) {
    return null;
  }

  // Show loading state
  if (marketGrade.loading) {
    return <LoadingCard matches={matches} />;
  }

  // Show error state if API failed (but not 404)
  if (marketGrade.isError) {
    return (
      <Card
        sx={{
          background: '#fff',
          borderRadius: 3,
          boxShadow: '0 2px 12px 0 rgba(16,30,54,.06)',
          border: '1px solid #E0E0E0',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Alert severity="info">{marketGrade.error} Please try again later.</Alert>
      </Card>
    );
  }

  // Fallback to original logic if no API data
  if (!marketGrade || !marketGrade.data) {
    return <></>;
  }

  // Custom circle size
  const circleSize = matches ? 70 : 100;
  const circleThickness = 4;
  const progressColor = COLOR_PRIMARY_2;
  const trackColor = '#E6EAF2';

  return (
    <Card
      sx={{
        background: '#fff',
        borderRadius: 3,
        boxShadow: '0 2px 12px 0 rgba(16,30,54,.06)',
        border: '1px solid #E0E0E0',
        overflow: 'visible',
        position: 'relative',
        p: 0,
        m: 0,
        minHeight: circleSize + 32,
        width: '100%',
        maxWidth: '100vw',
        // Top border accent
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: progressColor,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          zIndex: 2,
        },
      }}
    >
      <CardContent sx={{ p: matches ? 2 : 4, pr: matches ? 2 : 6 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: matches ? 'column' : 'row',
            alignItems: matches ? 'flex-start' : 'center',
            gap: matches ? 2 : 4,
            width: '100%',
          }}
        >
          {/* Circular Score Indicator */}
          <Box
            role="img"
            aria-label={`Market Grade: ${grade}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: circleSize,
              minWidth: circleSize,
              background: 'none',
              flexShrink: 0,
              ...(matches && {
                width: '100%',
                maxWidth: '100%',
              }),
            }}
          >
            {/* Gauge with number */}
            <Box sx={{ position: 'relative', width: circleSize, height: circleSize, mb: 0.5 }}>
              {/* Background track */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={circleSize}
                thickness={circleThickness}
                sx={{
                  color: trackColor,
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  zIndex: 0,
                }}
              />
              {/* Progress arc */}
              <CircularProgress
                variant="determinate"
                value={gradeScore}
                size={circleSize}
                thickness={circleThickness}
                sx={{
                  color: gradeColor,
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  zIndex: 1,
                  transition: 'all 0.5s',
                }}
              />
              {/* Grade number */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                <Typography
                  variant={matches ? 'h4' : 'h3'}
                  sx={{ 
                    fontWeight: 700, 
                    color: isGradeA ? undefined : COLOR_TEXT_1, 
                    fontSize: matches ? 32 : 40,
                    ...(isGradeA && getGradeAGradient()),
                  }}
                >
                  {grade}
                </Typography>
              </Box>
            </Box>
            {/* Market Grade label */}
            <Typography
              variant="caption"
              sx={{
                color: COLOR_TEXT_2,
                fontWeight: 500,
                mt: 1,
                fontSize: matches ? '12px' : '13px',
                letterSpacing: 0.2,
                textAlign: 'center',
                lineHeight: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Market Grade
              <span style={{ display: 'inline', marginLeft: '2px', marginTop: '2px' }}>
                <ConfigurableTooltip config={MARKET_FINDER_TOOLTIP_CONTENT.market_grade}>
                  <InfoOutlined color="primary" sx={{ fontSize: '14px' }} />
                </ConfigurableTooltip>
              </span>
            </Typography>
          </Box>

          {/* AI Summary Text */}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/* Market Summary Lines as bullet points */}
            <Box
              component="ul"
              sx={{ pl: 3, mb: 1, mt: 0, listStyle: 'disc', color: COLOR_TEXT_2 }}
            >
              {summaryLines.map((line, index) => (
                <li
                  key={index}
                  style={{ marginBottom: 8, fontSize: matches ? 14 : 16, lineHeight: 1.6 }}
                >
                  {renderWithBold(
                    getMetadataTextToAppend(index, line),
                    metadata,
                    handleOnSampleClick
                  )}
                </li>
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MarketGradeAIOverview;
